// src/app/app.routes.ts

import { Routes } from '@angular/router';
import { authGuard } from '@core/guards/auth.guard';
import { healthTravelGuard } from '@core/guards/health-travel.guard';
import { claimsGuard } from '@core/guards/claims.guard';
import { adminGuard } from '@core/guards/admin.guard';
import { warehouseGuard } from '@core/guards/warehouse.guard';
import { analyticsGuard } from '@core/guards/analytics.guard';
import { offersPoliciesGuard } from '@core/guards/offers-policies.guard';
import { invoicingGuard } from '@core/guards/invoicing.guard';
import { healthGuard } from '@core/guards/health.guard';
import { pyetesoriGuard } from '@core/guards/pyetesori.guard';
import { rpdGuard } from '@core/guards/rpd.guard';
import { directDebitGuard } from '@core/guards/direct-debit.guard';
import { demetGuard } from '@core/guards/demet.guard';
import { financaGuard } from '@core/guards/financa.guard';
import { sigurimetGuard } from '@core/guards/sigurimet.guard';

export const routes: Routes = [
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full'
  },

  // PUBLIC
  {
    path: 'login',
    loadComponent: () =>
      import('./features/auth/login/login.component').then(m => m.LoginComponent),
    title: 'Login | Insurance'
  },
  {
    path: 'sso',
    loadComponent: () =>
      import('./features/auth/sso/sso.component').then(m => m.SsoComponent),
    title: 'SSO | Insurance'
  },

  // PROTECTED
  {
    path: '',
    loadComponent: () =>
      import('./layout/main-layout/main-layout.component').then(
        m => m.MainLayoutComponent
      ),
    canActivate: [authGuard],
    children: [
      {
        path: 'dashboard',
        loadComponent: () =>
          import('./features/dashboard/dashboard.component').then(
            m => m.DashboardComponent
          ),
        title: 'Dashboard | Insurance',
        data: { breadcrumb: 'BREADCRUMB.DASHBOARD' }
      },

      {
        path: 'health-travel',
        canMatch: [healthTravelGuard],
        loadChildren: () =>
          import('./features/health-travel/health-travel.routes').then(
            m => m.healthTravelRoutes
          ),
        data: { breadcrumb: 'BREADCRUMB.HEALTH_TRAVEL' }
      },

      {
        path: 'claims',
        canMatch: [claimsGuard],
        loadChildren: () =>
          import('./features/claims/claims.routes').then(m => m.CLAIMS_ROUTES),
        data: { breadcrumb: 'BREADCRUMB.CLAIMS' }
      },

      {
        // Dëmet → TPL dhe të tjera (VFP menu Dëmet without health) — claims.tpl.*
        // keys. Separate from /claims (health), which stays untouched.
        path: 'demet',
        canMatch: [demetGuard],
        loadChildren: () =>
          import('./features/demet/demet.routes').then(m => m.DEMET_ROUTES),
        data: { breadcrumb: 'BREADCRUMB.DEMET' }
      },

      {
        // Financa (+ Pagat + Obligimet): VFP menu Financa and "AP të punëtorëve" —
        // finance.* keys (specifikimet 09-lejet). Pages behind financaMenuGuard.
        path: 'financa',
        canMatch: [financaGuard],
        loadChildren: () =>
          import('./features/financa/financa.routes').then(m => m.FINANCA_ROUTES),
        data: { breadcrumb: 'BREADCRUMB.FINANCA' }
      },

      {
        // Sigurimet: VFP menu Sigurimet — sigurimet.* keys (specifikimet lejet.md).
        // Pages behind sigurimetMenuGuard; the entries of other modules keep their routes.
        path: 'sigurimet',
        canMatch: [sigurimetGuard],
        loadChildren: () =>
          import('./features/sigurimet/sigurimet.routes').then(m => m.SIGURIMET_ROUTES),
        data: { breadcrumb: 'BREADCRUMB.SIGURIMET' }
      },

      {
        path: 'offers-policies',
        canMatch: [offersPoliciesGuard],
        loadChildren: () =>
          import('./features/offers-policies/offers-policies.routes').then(
            m => m.OFFERS_POLICIES_ROUTES
          ),
        data: { breadcrumb: 'BREADCRUMB.OFFERS_POLICIES' }
      },

      {
        path: 'invoicing',
        canMatch: [invoicingGuard],
        loadChildren: () =>
          import('./features/invoicing/invoicing.routes').then(
            m => m.INVOICING_ROUTES
          ),
        data: { breadcrumb: 'BREADCRUMB.INVOICING' }
      },

      {
        path: 'health',
        canMatch: [healthGuard],
        loadChildren: () =>
          import('./features/health/health.routes').then(m => m.HEALTH_ROUTES),
        data: { breadcrumb: 'BREADCRUMB.HEALTH' }
      },

      {
        path: 'profile',
        loadComponent: () =>
          import('./features/profile/profile.component').then(
            m => m.ProfileComponent
          ),
        title: 'Profile | Insurance',
        data: { breadcrumb: 'BREADCRUMB.PROFILE' }
      },

      {
        path: 'support',
        loadComponent: () =>
          import('./shared/components/support/support.component').then(
            m => m.SupportComponent
          ),
        title: 'Support | Insurance',
        data: { breadcrumb: 'BREADCRUMB.SUPPORT' }
      },

      {
        path: 'analytics',
        canMatch: [analyticsGuard],
        loadChildren: () =>
          import('./features/analytics/analytics.routes').then(m => m.ANALYTICS_ROUTES),
        data: { breadcrumb: 'BREADCRUMB.ANALYTICS' }
      },

      {
        path: 'warehouse',
        canMatch: [warehouseGuard],
        loadChildren: () =>
          import('./features/warehouse/warehouse.routes').then(m => m.WAREHOUSE_ROUTES),
        data: { breadcrumb: 'BREADCRUMB.WAREHOUSE' }
      },

      {
        path: 'pyetesori',
        canMatch: [pyetesoriGuard],
        loadChildren: () =>
          import('./features/pyetesori-admin/pyetesori-admin.routes').then(
            m => m.PYETESORI_ADMIN_ROUTES
          ),
        data: { breadcrumb: 'BREADCRUMB.PYETESORI' }
      },

      {
        // Sigurimet → RPD (Raporti i Policave të Dorëzuara)
        path: 'rpd',
        canMatch: [rpdGuard],
        loadChildren: () =>
          import('./features/rpd/rpd.routes').then(m => m.RPD_ROUTES),
        data: { breadcrumb: 'BREADCRUMB.RPD' }
      },

      {
        // Sigurimet → Fletëpagesat (VFP menus 229–231). Every child route is gated by the
        // sigurimet.fletepagesat.* keys, like VFP (user decision 29.09.2026, lejet.md §2.8a):
        // sigurimetMenuGuard (fletepagesat_kerkimi / fletepagesa_hyrja / fletepagesat_raporti)
        // and fletepagesaDetajetGuard on ':id' — see fletepagesat.routes.ts.
        path: 'fletepagesat',
        loadChildren: () =>
          import('./features/fletepagesat/fletepagesat.routes').then(m => m.FLETEPAGESAT_ROUTES),
        data: { breadcrumb: 'BREADCRUMB.FLETEPAGESAT' }
      },

      {
        // Sigurimet → Regjistrimi i pagesave (VFP menu 20). Every child route is gated by the
        // sigurimet.pagesat.* keys, like VFP (user decision 29.09.2026, lejet.md §2.8a):
        // sigurimetMenuGuard (pagesat_regjistrimi / pagesat_papaguara) and pagesatRaportiGuard
        // on 'raporti' — see regjistrimi-pagesave.routes.ts. Correcting/deleting a payment
        // still needs payments.edit / payments.delete inside the screens.
        path: 'regjistrimi-pagesave',
        loadChildren: () =>
          import('./features/regjistrimi-pagesave/regjistrimi-pagesave.routes').then(
            m => m.REGJISTRIMI_PAGESAVE_ROUTES
          ),
        data: { breadcrumb: 'BREADCRUMB.REGJ_PAGESA' }
      },

      {
        // Sigurimet → Direct Debit — directdebit.view / directdebit.manage.
        path: 'direct-debit',
        canMatch: [directDebitGuard],
        loadChildren: () =>
          import('./features/direct-debit/direct-debit.routes').then(m => m.DIRECT_DEBIT_ROUTES),
        data: { breadcrumb: 'BREADCRUMB.DIRECT_DEBIT' }
      },

      {
        path: 'admin',
        canMatch: [adminGuard],
        loadChildren: () =>
          import('./features/admin/admin.routes').then(m => m.ADMIN_ROUTES),
        data: { breadcrumb: 'BREADCRUMB.ADMIN' }
      }
    ]
  },

  {
    path: '**',
    redirectTo: 'dashboard'
  }
];
